import { InsightsSupply, SubcategoryRow } from '../../types/marketing';
import DataTable, { DataTableColumn } from './DataTable';
import FunnelBar from './FunnelBar';
import { rateFromPrevious, stepLabel } from './marketingMath';

export const DEMAND_TOOLTIP = 'GA4 demand dimensions are not registered yet';

const columns = (dims: boolean): DataTableColumn<SubcategoryRow>[] => [
  { key: 'name', header: 'Subcategory', accessor: (r) => r.name || r.subcategoryId, format: 'text' },
  { key: 'liveServices', header: 'Live services', accessor: (r) => r.liveServices, format: 'int' },
  { key: 'subcategoryTaps', header: 'Taps in app', accessor: (r) => r.subcategoryTaps, format: 'int', tooltipWhenNull: dims ? undefined : DEMAND_TOOLTIP },
  { key: 'zeroResultSearches', header: 'Zero-result searches', accessor: (r) => r.zeroResultSearches, format: 'int', tooltipWhenNull: dims ? undefined : DEMAND_TOOLTIP },
  { key: 'gap', header: 'Gap', accessor: (r) => (r.gap ? 'Gap: demand, no live service' : 'No gap'), format: 'text' },
];

function GapIcon() {
  return (
    <svg className="mkt-gap__icon" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8 1.8l6.4 11.4H1.6L8 1.8z" fill="none" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M8 6.3v3.2M8 11.3v.2" fill="none" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/** Spec 084 Supply tab — garage acquisition funnel and live services vs demand per subcategory (gaps flagged). */
export default function SupplySection({ data, range }: { data: InsightsSupply; range: { from: string; to: string; timezone: string } }) {
  const steps = data.funnel.map((s, i) => ({
    key: s.step, label: stepLabel(s.step), count: s.count,
    rateFromPrevious: i === 0 ? null : rateFromPrevious(s.count, data.funnel[i - 1].count),
    nullReason: s.count == null ? 'Source not connected or no data for this range' : undefined,
  }));
  const gaps = data.subcategories.filter((s) => s.gap);
  const dims = data.ga4Dims.registered;
  return (
    <div className="mkt-section" data-testid="section-supply">
      <section className="mkt-card"><FunnelBar title="Garage supply" steps={steps} testId="supply-funnel" /></section>
      <section className="mkt-card">
        {!dims && (
          <p className="mkt-note" data-testid="supply-dims-note">
            Demand columns not connected: register the GA4 custom dimensions search_scope, result_count and item_category2 to fill them.
          </p>
        )}
        {gaps.length > 0 && (
          <ul className="mkt-gaps" data-testid="supply-gaps" aria-label="Subcategories with demand and no live service">
            {gaps.map((g) => <li key={g.subcategoryId} className="mkt-gap"><GapIcon /><span>Gap: {g.name || g.subcategoryId}</span></li>)}
          </ul>
        )}
        <DataTable caption="Subcategories" columns={columns(dims)} rows={data.subcategories} rowKey={(r) => r.subcategoryId}
          defaultSort={{ key: 'liveServices', dir: 'asc' }} emptyText="No subcategories" exportMeta={range} testId="supply-subcategories" />
      </section>
    </div>
  );
}
